import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { fmtError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Search, Loader2, BadgeCheck, Crown, MessageCircle, UserPlus, Users, Sparkles } from "lucide-react";
import FriendButton from "@/components/app/FriendButton";

export default function SearchPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const nav = useNavigate();

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [filter, setFilter] = useState("all"); // "all" | "verified" | "vip"

  const performSearch = async (queryVal) => {
    const q = typeof queryVal === "string" ? queryVal : searchQuery;
    setSearching(true);
    try {
      const { data } = await api.get(`/users/search?q=${encodeURIComponent(q.trim())}`);
      setSearchResults(data);
    } catch (err) {
      toast.error(fmtError(err.response?.data?.detail) || "Failed to search users.");
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    performSearch("");
  }, []);

  const startDM = async (uid) => {
    try {
      const { data } = await api.post(`/chats/direct/${uid}`);
      nav(`/chats/${data.id}`);
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || "Could not start chat");
    }
  };

  const filteredUsers = searchResults.filter((u) => {
    if (filter === "verified") return u.verified;
    if (filter === "vip") return u.vip || u.is_vip;
    return true;
  });

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 pb-32" data-testid="search-page">
      <button onClick={() => nav(-1)} className="btn-nb mb-5" data-testid="search-back-btn">
        <ArrowLeft className="w-4 h-4" strokeWidth={2.5} /> {t("common.back")}
      </button>

      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mb-2">
        Find <span className="bg-gradient-to-r from-[#0F9D8F] via-[#14C8B0] to-[#5EEAD4] bg-clip-text text-transparent">Everyone</span>
      </h1>
      <p className="text-[#4A4F58] text-sm sm:text-base mb-5 font-semibold">
        Search community members by name or @username, view their profile, and connect.
      </p>

      {/* Search Input Bar & Button */}
      <form onSubmit={(e) => { e.preventDefault(); performSearch(searchQuery); }} className="mb-5 flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-[#7C828D]" strokeWidth={2.5} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              const val = e.target.value;
              setSearchQuery(val);
              performSearch(val);
            }}
            placeholder="Search by name or @username..."
            className="w-full bg-white border-2 border-[#121212] rounded-2xl pl-11 pr-14 py-3.5 text-sm sm:text-base font-semibold placeholder-[#7C828D] outline-none shadow-nb-sm focus:border-[#0F9D8F] transition-all"
            data-testid="global-user-search-input"
            autoFocus
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => { setSearchQuery(""); performSearch(""); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-[#7C828D] hover:text-black bg-[#F4F1E8] px-2 py-1 rounded-lg"
            >
              Clear
            </button>
          )}
        </div>
        <button
          type="submit"
          disabled={searching}
          className="bg-[#00CED1] hover:bg-[#14C8B0] text-black font-black px-5 py-3.5 rounded-2xl border-2 border-[#121212] shadow-nb-sm disabled:opacity-50 flex items-center gap-2 shrink-0 text-sm"
          data-testid="global-user-search-btn"
        >
          {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" strokeWidth={3} />}
          Search
        </button>
      </form>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6 p-1 bg-[#F4F2ED] rounded-full border border-[#E8E5DD] w-fit">
        {[
          { id: "all", label: `All Users (${searchResults.length})` },
          { id: "verified", label: `Verified 🔐` },
          { id: "vip", label: `VIP Members 👑` },
        ].map((item) => (
          <button
            key={item.id}
            onClick={() => setFilter(item.id)}
            data-testid={`filter-${item.id}`}
            className={`px-4 py-1.5 rounded-full font-display font-semibold text-xs tracking-tight transition-all ${
              filter === item.id ? "bg-[#0F1115] text-white shadow-soft" : "text-[#4A4F58]"
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {/* Results List */}
      {searching && searchResults.length === 0 ? (
        <div className="p-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#0F9D8F] mx-auto mb-2" />
          <p className="text-sm font-bold text-[#7C828D]">Searching profiles...</p>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="card-nb p-10 text-center bg-[#F4F2ED]">
          <Users className="w-12 h-12 mx-auto mb-3 text-[#7C828D]" strokeWidth={2.25} />
          <h3 className="font-display font-extrabold text-xl mb-1 tracking-tight">No users found</h3>
          <p className="text-[#4A4F58] text-sm">
            {searchQuery ? `No profiles matched "${searchQuery}".` : "No users found in this category."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="search-results-grid">
          {filteredUsers.map((u) => (
            <div
              key={u.id}
              className="card-nb p-4 bg-white flex flex-col justify-between gap-3 border-2 border-[#121212] shadow-nb-sm"
              data-testid={`user-card-${u.id}`}
            >
              <Link to={`/u/${u.id}`} className="flex items-start gap-3">
                <div className="relative shrink-0">
                  <div className="w-12 h-12 rounded-full bg-[#E0F7F6] border border-[#121212] shadow-nb-sm flex items-center justify-center font-display font-bold text-lg overflow-hidden">
                    {u.avatar_url ? <img src={u.avatar_url} alt="" className="w-full h-full object-cover" /> : u.name?.[0]?.toUpperCase()}
                  </div>
                  {u.verified && (
                    <span className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 shadow">
                      <BadgeCheck className="w-4 h-4 text-[#00B4D8]" strokeWidth={2.5} />
                    </span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-bold text-base flex items-center gap-1.5 truncate">
                    <span>{u.name}</span>
                    {u.vip && <Crown className="w-4 h-4 text-[#F9C349]" strokeWidth={2.5} fill="#F9C349" />}
                  </div>
                  <div className="text-xs font-bold text-[#0F9D8F] tracking-tight">
                    @{u.username || u.name?.toLowerCase().replace(/\s+/g, "_")}
                  </div>
                  <div className="text-xs text-[#7C828D] truncate mt-0.5">
                    {u.age ? `${u.age} y/o` : ""} {u.gender ? `· ${u.gender}` : ""} {u.location ? `· ${u.location}` : ""}
                  </div>
                </div>
              </Link>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 pt-2 border-t border-[#F4F1E8]">
                <Link
                  to={`/u/${u.id}`}
                  className="btn-nb !py-1.5 !px-3 text-xs font-bold flex-1 text-center bg-[#F4F1E8] hover:bg-[#E9E5D7] text-black border border-black"
                  data-testid={`view-profile-btn-${u.id}`}
                >
                  View Profile
                </Link>

                <FriendButton
                  userId={u.id}
                  status={typeof u.friend_status === "string" ? u.friend_status : u.friend_status?.status}
                  compact
                  onChange={() => performSearch(searchQuery)}
                />

                <button
                  onClick={() => startDM(u.id)}
                  className="btn-nb !py-1.5 !px-2.5 bg-[#00CED1] hover:bg-[#14C8B0] text-black"
                  title="Message user"
                  data-testid={`message-btn-${u.id}`}
                >
                  <MessageCircle className="w-4 h-4" strokeWidth={2.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
